import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { orderAPI } from '../../../utils/api';

const Cart = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showSuccess, showError } = useNotification();
    const [cart, setCart] = useState([]);
    const [loading, setLoading] = useState(false);
    const [address, setAddress] = useState({
        street: '',
        city: '',
        state: '',
        zip: '',
        country: 'India'
    });

    useEffect(() => {
        // Load cart from local storage
        const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
        setCart(savedCart);
    }, []);

    const updateQuantity = (index, delta) => {
        const newCart = [...cart];
        newCart[index].quantity += delta;
        if (newCart[index].quantity <= 0) {
            newCart.splice(index, 1);
        }
        setCart(newCart);
        localStorage.setItem('cart', JSON.stringify(newCart));
    };

    const removeItem = (index) => {
        const newCart = [...cart];
        newCart.splice(index, 1);
        setCart(newCart);
        localStorage.setItem('cart', JSON.stringify(newCart));
    };

    const calculateTotal = () => {
        return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    const handleAddressChange = (e) => {
        setAddress({ ...address, [e.target.name]: e.target.value });
    };

    const handleCheckout = async () => {
        // Validate inputs
        if (cart.length === 0) return showError('Cart is empty');
        if (!address.street || !address.city || !address.state || !address.zip) {
            return showError('Please fill in all address fields');
        }

        // Group items by Retailer (Currently simplistic: assuming 1 retailer per order or creating multiple orders?
        // Implementation: For MVP, create 1 order per retailer.
        // Logic: Identify unique retailers.

        // For now, let's assume the cart handles ONE retailer at a time or we split logic.
        // Simplest approach: Create separate order API calls for mixed retailers OR refuse mixed cart.
        // Let's iterate distinct retailers.

        const retailers = [...new Set(cart.map(item => item.retailer_id))];

        setLoading(true);
        try {
            if (retailers.length > 1) {
                showError('Please place separate orders for items from different suppliers.');
                setLoading(false);
                return;
            }

            const orderData = {
                items: cart,
                shipping_address: address,
                retailer_id: retailers[0]
            };

            const createRes = await orderAPI.create(orderData);
            if (!createRes.data.success) throw new Error('Order creation failed');

            const { order_id, razorpay_order_id, amount, key_id } = createRes.data.data;

            const options = {
                key: key_id,
                amount: amount * 100,
                currency: "INR",
                name: "Vivara",
                description: "Product Purchase",
                order_id: razorpay_order_id,
                handler: async (response) => {
                    try {
                        if (!response.razorpay_order_id || !response.razorpay_payment_id || !response.razorpay_signature) {
                            showError('Payment verification failed. Missing payment details.');
                            return;
                        }
                        await orderAPI.verify({
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            order_id: order_id
                        });
                        showSuccess(`Order placed successfully!`);

                        setCart([]);
                        localStorage.removeItem('cart');
                        navigate('/patient/orders');

                    } catch (err) {
                        showError('Payment verification failed');
                    }
                },
                prefill: {
                    name: user?.name || '',
                    email: user?.email || '',
                    contact: user?.phone || ''
                },
                theme: {
                    color: "#D4AF37"
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();

        } catch (error) {
            console.error(error);
            showError('Checkout failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <h1 className="text-2xl font-bold text-gold">Shopping Cart</h1>

            {cart.length === 0 ? (
                <div className="text-center py-12 bg-gray-800 rounded-xl">
                    <p className="text-gray-400 mb-4">Your cart is empty</p>
                    <button
                        onClick={() => navigate('/patient/medicines')}
                        className="bg-gold text-dark px-6 py-2 rounded-lg font-medium"
                    >
                        Browse Products
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Cart Items */}
                    <div className="space-y-4">
                        {cart.map((item, index) => (
                            <div key={index} className="bg-gray-800 p-4 rounded-xl flex justify-between items-center">
                                <div>
                                    <h3 className="font-semibold">{item.name}</h3>
                                    <p className="text-gray-400 text-sm">₹{item.price} x {item.quantity}</p>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <button
                                        onClick={() => updateQuantity(index, -1)}
                                        className="w-8 h-8 rounded-full bg-gray-700 text-white flex items-center justify-center"
                                    >
                                        -
                                    </button>
                                    <span>{item.quantity}</span>
                                    <button
                                        onClick={() => updateQuantity(index, 1)}
                                        className="w-8 h-8 rounded-full bg-gray-700 text-white flex items-center justify-center"
                                    >
                                        +
                                    </button>
                                    <button
                                        onClick={() => removeItem(index)}
                                        className="text-yellow-400 ml-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}

                        <div className="pt-4 border-t border-gray-700">
                            <div className="flex justify-between text-xl font-bold">
                                <span>Total</span>
                                <span className="text-gold">₹{calculateTotal()}</span>
                            </div>
                        </div>
                    </div>

                    {/* Delivery Address */}
                    <div className="bg-gray-800 p-6 rounded-xl h-fit">
                        <h2 className="text-xl font-semibold mb-4 text-gold">Delivery Address</h2>
                        <div className="space-y-3">
                            <input
                                type="text"
                                name="street"
                                placeholder="Street Address / Flat No"
                                value={address.street}
                                onChange={handleAddressChange}
                                className="w-full bg-gray-700 rounded-lg px-4 py-3 border border-gray-600 focus:border-gold outline-none"
                            />
                            <div className="grid grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    name="city"
                                    placeholder="City"
                                    value={address.city}
                                    onChange={handleAddressChange}
                                    className="w-full bg-gray-700 rounded-lg px-4 py-3 border border-gray-600 focus:border-gold outline-none"
                                />
                                <input
                                    type="text"
                                    name="zip"
                                    placeholder="PIN Code"
                                    value={address.zip}
                                    onChange={handleAddressChange}
                                    className="w-full bg-gray-700 rounded-lg px-4 py-3 border border-gray-600 focus:border-gold outline-none"
                                />
                            </div>
                            <input
                                type="text"
                                name="state"
                                placeholder="State"
                                value={address.state}
                                onChange={handleAddressChange}
                                className="w-full bg-gray-700 rounded-lg px-4 py-3 border border-gray-600 focus:border-gold outline-none"
                            />
                            <button
                                onClick={handleCheckout}
                                disabled={loading}
                                className="w-full bg-gold text-dark font-bold py-4 rounded-xl hover:opacity-90 transition mt-4"
                            >
                                {loading ? 'Processing...' : 'Pay & Order'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Cart;
